/* Sample-claim cart guard.
   ============================================================================
   Phase 5B Commit 5B-4 — checkout-button intercept for sample-offer carts.

   The structural enforcement "exactly 1 RISE 4-pack + 1 DRIFT 4-pack" cannot
   live in a Shopify Discount Function on a Basic-plan store (EC-035: custom-
   app Functions are Shopify-Plus-only). The proven mitigation in
   case-aggregation.js — a client-side guard chained into circadia-chrome.js's
   checkout-button click — is the model. This module mirrors it for the
   sample-offer flow.

   Read flow (called by circadia-chrome.js on checkout-button click):

     1. Fetch /cart.js for the current cart state + attributes.
     2. If cart.attributes.sample_claim_id is absent → return
        { proceed:true, removedDiscount:false, reason:'not-a-sample-claim' }
        (not a sample-offer cart; let the other guards / native checkout run).
     3. Read cart.attributes.sample_expected_variants — JSON with the locked
        2 variant GIDs the customer picked at claim time. If absent / parse-
        fail / version-mismatch → clearSampleApplied() + return
        { proceed:true, removedDiscount:true, reason:'parse-error-cleared' }.
     4. Compare the cart's line items against the expected pair:
          - exactly 1 line with variant_id == numeric(rise_variant_gid), qty 1
          - exactly 1 line with variant_id == numeric(drift_variant_gid), qty 1
        Extra non-sample line items (merch, etc.) DO NOT trigger a clear —
        the discount is product-scoped to the 6 launch flavors, so anything
        else the customer added pays full price normally.
     5. Match → proceed. Mismatch → clearSampleApplied() + return
        { proceed:true, removedDiscount:true, reason:'cart-shape-mismatch' }.

   Soft guard contract — proceed is ALWAYS true. The guard never blocks the
   customer from continuing to checkout; the worst it does is silently strip
   a stale discount + show the chrome's toast. orders/paid (5B-6) is the
   server-side backstop.

   No predictor/engine round-trip needed: the expected shape is in the cart
   attribute (locked at claim time), the actual shape is in cart.items, the
   comparison is a few-line iteration. Different from case-aggregation.js's
   D3 strategy because the rule here is "exactly these 2 variants," not a
   derived case-count threshold.

   Window namespace: window.Circadia.sampleClaim.guardCheckout. Parallel to
   window.Circadia.caseAggregation.guardCheckout — circadia-chrome.js chains
   both on each checkout click.

   ----------------------------------------------------------------------------
   No external dependencies. IIFE, ES5-ish for broad browser support, matches
   the surrounding circadia-chrome.js + case-aggregation.js style. */

(function () {
  'use strict';

  // ─── Config ────────────────────────────────────────────────────────────────

  var ATTR_CLAIM_ID = 'sample_claim_id';
  var ATTR_EXPECTED = 'sample_expected_variants';
  var EXPECTED_SCHEMA_VERSION = 1;

  // ─── Network helpers ───────────────────────────────────────────────────────

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function postCartUpdate(body) {
    var url = (window.routes && window.routes.cart_update_url ? window.routes.cart_update_url : '/cart/update') + '.js';
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then(function (r) { return r.json(); });
  }

  /** Clear the sample-discount + both cart attributes via a single
   *  /cart/update.js call. Mirrors case-aggregation.js's clearApplied(). */
  function clearSampleApplied() {
    var attributes = {};
    attributes[ATTR_CLAIM_ID] = '';
    attributes[ATTR_EXPECTED] = '';
    return postCartUpdate({ discount: '', attributes: attributes });
  }

  // ─── Cart-attribute reader ─────────────────────────────────────────────────

  function getAttr(cart, name) {
    if (!cart || !cart.attributes) return '';
    var v = cart.attributes[name];
    return v == null ? '' : String(v);
  }

  // ─── Variant GID → numeric ID (cart line items use numeric IDs) ────────────

  function variantIdFromGid(gid) {
    if (typeof gid !== 'string') return null;
    var m = /^gid:\/\/shopify\/ProductVariant\/(\d+)$/.exec(gid);
    if (!m) return null;
    var n = parseInt(m[1], 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  // ─── Shape comparator ──────────────────────────────────────────────────────

  /**
   * Returns true iff the cart contains EXACTLY 1 line with the expected
   * RISE variant (qty 1) AND EXACTLY 1 line with the expected DRIFT
   * variant (qty 1). Additional non-sample lines do NOT disqualify — the
   * SAMPLE-{16hex} discount is product-scoped to the 6 launch GIDs, so
   * anything else pays full price normally.
   *
   * Notes:
   *   - We sum quantity across lines per variant (defends against the
   *     unlikely case of a cart having two separate lines for the same
   *     variant — Shopify usually merges, but be defensive).
   *   - qty must be exactly 1 of each. qty 2 of the expected RISE counts
   *     as a mismatch (customer added another at full price; the $40
   *     discount would still apply, but the spec is 1+1 — clear it).
   */
  function cartShapeMatches(cart, riseGid, driftGid) {
    var riseNum = variantIdFromGid(riseGid);
    var driftNum = variantIdFromGid(driftGid);
    if (!riseNum || !driftNum) return false;
    if (riseNum === driftNum) return false; // pathological: same variant twice

    var riseCount = 0;
    var driftCount = 0;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!item || typeof item.variant_id !== 'number') continue;
      var qty = item.quantity || 0;
      if (item.variant_id === riseNum)  riseCount  += qty;
      if (item.variant_id === driftNum) driftCount += qty;
    }
    return riseCount === 1 && driftCount === 1;
  }

  // ─── Main guard ────────────────────────────────────────────────────────────

  var guardInFlight = false;

  /**
   * @returns {Promise<{ proceed: boolean, removedDiscount: boolean, reason: string }>}
   *   reason ∈ { "guard-already-in-flight", "not-a-sample-claim",
   *              "parse-error-cleared", "shape-match",
   *              "cart-shape-mismatch", "fetch-failed-fail-open" }
   *   proceed is ALWAYS true (soft guard, never blocks the customer).
   */
  async function guardCheckout() {
    if (guardInFlight) {
      return { proceed: true, removedDiscount: false, reason: 'guard-already-in-flight' };
    }
    guardInFlight = true;
    try {
      var cart;
      try {
        cart = await fetchCart();
      } catch (err) {
        // /cart.js outage — fail-open, mirror case-aggregation's posture.
        // orders/paid (5B-6) is the server-side backstop if a stale code
        // sneaks through.
        if (window.console && window.console.warn) {
          console.warn('[sample-claim-guard] /cart.js fetch failed, failing open:',
            err && err.message ? err.message : err);
        }
        return { proceed: true, removedDiscount: false, reason: 'fetch-failed-fail-open' };
      }

      var claimId = getAttr(cart, ATTR_CLAIM_ID);
      if (!claimId) {
        return { proceed: true, removedDiscount: false, reason: 'not-a-sample-claim' };
      }

      var expectedRaw = getAttr(cart, ATTR_EXPECTED);
      if (!expectedRaw) {
        // claim_id set but expected payload missing — corrupted state,
        // strip the discount + attributes defensively.
        await safelyClear();
        return { proceed: true, removedDiscount: true, reason: 'parse-error-cleared' };
      }

      var expected;
      try {
        expected = JSON.parse(expectedRaw);
      } catch (_) {
        await safelyClear();
        return { proceed: true, removedDiscount: true, reason: 'parse-error-cleared' };
      }
      if (
        !expected ||
        expected.version !== EXPECTED_SCHEMA_VERSION ||
        typeof expected.rise_variant_gid !== 'string' ||
        typeof expected.drift_variant_gid !== 'string'
      ) {
        await safelyClear();
        return { proceed: true, removedDiscount: true, reason: 'parse-error-cleared' };
      }

      if (cartShapeMatches(cart, expected.rise_variant_gid, expected.drift_variant_gid)) {
        return { proceed: true, removedDiscount: false, reason: 'shape-match' };
      }

      // Mismatch — strip the discount + attributes and proceed at full
      // price (whatever items the customer assembled).
      await safelyClear();
      return { proceed: true, removedDiscount: true, reason: 'cart-shape-mismatch' };
    } finally {
      guardInFlight = false;
    }
  }

  /** Best-effort clear — never throws upward. A clear failure (rare; e.g.
   *  /cart/update.js outage) gets logged + ignored. orders/paid is the
   *  server-side backstop if a stale code sneaks through. */
  async function safelyClear() {
    try {
      await clearSampleApplied();
    } catch (err) {
      if (window.console && window.console.warn) {
        console.warn('[sample-claim-guard] clearSampleApplied failed:',
          err && err.message ? err.message : err);
      }
    }
  }

  // ─── Window export ─────────────────────────────────────────────────────────
  //
  // Namespaced under window.Circadia.sampleClaim so it doesn't collide
  // with window.Circadia.caseAggregation. circadia-chrome.js's checkout-
  // click handler defensively checks for this export and skips the guard
  // if the module didn't load — proceed-without-guard is the fallback.

  if (!window.Circadia) window.Circadia = {};
  if (!window.Circadia.sampleClaim) window.Circadia.sampleClaim = {};
  window.Circadia.sampleClaim.guardCheckout = guardCheckout;
})();
